import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { create } from 'zustand';

import { getServiceId } from '@/api/tokens.helper';
import { useRoles } from '@/hooks/useRoles';
import { usePerson } from '@/store/persons/person.store';
import { OWNER_GROUP_ROLES, UserRole } from '@/utils/roles';
import { toast } from '@/utils/toast';

import {
  acceptSuggestion,
  dismissSuggestion,
  getHolidays,
  getLiveSuggestions,
  markSuggestionSeen,
  requestHolidayIdea,
  snoozeSuggestion,
} from './proactive.api';
import {
  type CalendarHolidayInfo,
  type HabitSuggestion,
  type HolidaySuggestion,
  mapHoliday,
  mapHolidaySuggestion,
  mapSuggestions,
  type ProactiveSuggestion,
} from './proactive.mappers';

/** core-ai shows suggestions to the people who plan for the vessel — same as its memory page. */
const PLANNER_ROLES = [...OWNER_GROUP_ROLES, UserRole.CAPTAIN];

const STALE_TIME = 5 * 60 * 1000;

export const proactiveKeys = {
  live: (vesselId: string | null) => ['proactive-suggestions', vesselId] as const,
  holidays: (year: number) => ['proactive-holidays', year] as const,
};

/** Whether the current person may ask for and act on AI ideas — holidays, habits (owner group / captain). */
export const useIsHolidayPlanner = () => useSuggestionAudience().allowed;

/** Who is asking, and whether they may ask at all. */
const useSuggestionAudience = () => {
  const { currentUser } = usePerson();
  const { hasAnyRole } = useRoles();
  const vesselId = getServiceId();
  const userId = currentUser?.id ?? null;
  return { vesselId, userId, allowed: !!vesselId && !!userId && hasAnyRole(PLANNER_ROLES) };
};

/**
 * Open and snoozed suggestions of the current vessel (holiday ideas, habits).
 * Empty for anyone who is not owner/captain — the endpoint would answer 403, so
 * it is never asked.
 */
export const useProactiveSuggestionsQuery = () => {
  const { vesselId, userId, allowed } = useSuggestionAudience();
  return useQuery({
    queryKey: proactiveKeys.live(vesselId),
    queryFn: async () => {
      try {
        return mapSuggestions(await getLiveSuggestions(vesselId!, userId!));
      } catch (error) {
        // A role change can land between the check above and the call; that is
        // "nothing to show", not an error for the user.
        if (axios.isAxiosError(error) && error.response?.status === 403) return [];
        throw error;
      }
    },
    enabled: allowed,
    staleTime: STALE_TIME,
  });
};

const useSuggestionMutation = <TArgs extends { id: string }>(
  fn: (args: TArgs & { userId: string }) => Promise<unknown>,
  errorMessage: string | null,
) => {
  const queryClient = useQueryClient();
  const { vesselId, userId } = useSuggestionAudience();
  return useMutation({
    mutationFn: (args: TArgs) => {
      if (!userId) return Promise.reject(new Error('No current user'));
      return fn({ ...args, userId });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: proactiveKeys.live(vesselId) }),
    onError: (error) => {
      console.error(error);
      if (errorMessage) toast.error(errorMessage);
    },
  });
};

/** After the one-time modal was shown. Silent on failure: the worst case is seeing it once more. */
export const useMarkSuggestionSeenMutation = () =>
  useSuggestionMutation<{ id: string }>(({ id, userId }) => markSuggestionSeen(id, userId), null);

export const useAcceptSuggestionMutation = () =>
  useSuggestionMutation<{ id: string; eventId?: string }>(
    ({ id, userId, eventId }) =>
      acceptSuggestion(id, userId, eventId ? `event:${eventId}` : undefined),
    'Couldn’t link the event. Please try again.',
  );

export const useSnoozeSuggestionMutation = () =>
  useSuggestionMutation<{ id: string; until: Date }>(
    ({ id, userId, until }) => snoozeSuggestion(id, userId, until),
    'Couldn’t set the reminder. Please try again.',
  );

/**
 * An idea for an upcoming holiday the person clicked in the calendar. Resolves to
 * the suggestion to open (the existing one, a dismissed one brought back, or a
 * new one written on the spot — a few seconds).
 */
export const useRequestHolidayIdeaMutation = () => {
  const queryClient = useQueryClient();
  const { vesselId, userId } = useSuggestionAudience();
  return useMutation({
    mutationFn: async ({ key, year }: { key: string; year: number }) => {
      if (!vesselId || !userId) throw new Error('No current vessel or user');
      const suggestion = mapHolidaySuggestion(
        await requestHolidayIdea(vesselId, key, userId, year),
      );
      if (!suggestion) throw new Error('Unexpected suggestion shape');
      return suggestion;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: proactiveKeys.live(vesselId) }),
    onError: (error) => {
      console.error(error);
      toast.error('Couldn’t get a holiday idea. Please try again.');
    },
  });
};

/**
 * The US holidays of the given years (federal + the celebration ones the agent
 * has ideas for). Static per year, so fetched once per session.
 */
export const useHolidaysByYears = (years: number[]): CalendarHolidayInfo[] => {
  const results = useQueries({
    queries: years.map((year) => ({
      queryKey: proactiveKeys.holidays(year),
      queryFn: async () => (await getHolidays(year)).map(mapHoliday),
      staleTime: Infinity,
      retry: 1,
    })),
  });
  return results.flatMap((r) => r.data ?? []);
};

export const useDismissSuggestionMutation = () =>
  useSuggestionMutation<{ id: string; muteTopic?: boolean; muteKind?: boolean }>(
    ({ id, userId, muteTopic, muteKind }) =>
      dismissSuggestion(id, { user_id: userId, mute_topic: muteTopic, mute_kind: muteKind }),
    'Couldn’t dismiss the suggestion. Please try again.',
  );

/** Same-day key for matching a suggestion to a calendar cell. */
export const dayKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

/** Live holiday ideas by the calendar day their badge sits on. */
export const useHolidaySuggestionsByDay = (): Map<string, HolidaySuggestion> => {
  const { data } = useProactiveSuggestionsQuery();
  const byDay = new Map<string, HolidaySuggestion>();
  for (const suggestion of data ?? [])
    if (suggestion.kind === 'holiday')
      byDay.set(dayKey(suggestion.holiday.observedDate), suggestion);
  return byDay;
};

/** Live habit suggestions by the day they are about. */
export const useHabitSuggestionsByDay = (): Map<string, HabitSuggestion> => {
  const { data } = useProactiveSuggestionsQuery();
  const byDay = new Map<string, HabitSuggestion>();
  for (const suggestion of data ?? [])
    if (suggestion.kind === 'habit') byDay.set(dayKey(suggestion.date), suggestion);
  return byDay;
};

/** A holiday to plan without an AI idea: the wizard opens on that day with its name. */
export type BlankHolidayPlan = { title: string; date: Date };

type HolidaySuggestionUiState = {
  /** The suggestion whose card is open — from the arrival modal or a calendar click. */
  openId: string | null;
  /**
   * The opened suggestion as it was handed over (an idea fetched on request), so
   * the card shows at once instead of waiting for the live list to refetch.
   */
  openSnapshot: ProactiveSuggestion | null;
  /** The suggestion being turned into an event in the wizard. */
  planningId: string | null;
  /** A holiday being planned with no idea behind it (commemorative days, the day itself). */
  blankPlan: BlankHolidayPlan | null;
  open: (id: string, snapshot?: ProactiveSuggestion) => void;
  close: () => void;
  startPlanning: (id: string) => void;
  planBlank: (plan: BlankHolidayPlan) => void;
  stopPlanning: () => void;
};

/** Client-only state: which card is open. Server data stays in react-query. */
export const useHolidaySuggestionUi = create<HolidaySuggestionUiState>((set) => ({
  openId: null,
  openSnapshot: null,
  planningId: null,
  blankPlan: null,
  open: (id, snapshot) => set({ openId: id, openSnapshot: snapshot ?? null }),
  close: () => set({ openId: null, openSnapshot: null }),
  startPlanning: (id) => set({ openId: null, planningId: id, blankPlan: null }),
  planBlank: (plan) => set({ openId: null, planningId: null, blankPlan: plan }),
  stopPlanning: () => set({ planningId: null, blankPlan: null }),
}));
